import { useContext, useEffect, useState } from 'react';
import { onSnapshot, orderBy } from 'firebase/firestore';
import { FirebaseContext } from '@/session/FirebaseContext';
import { sortGameResultsRecentFirst, type Dict, toDict } from '@/helpers';
import { dedupeGameResults } from '@/helpers/roster/dedupeGameResults';
import { type gameResult, type gameUpdate, queryGameUpdates, sentinelType } from '@/models/gameUpdate';

/**
 * Live WC-26 match results from `competitions/wc-26/gameUpdates/*`.
 * Rules require a signed-in user; guests get an empty list (subscription errors are logged).
 * Most recent kickoff first.
 */
export function useWc26GameResults(): gameResult[] {
  const ctx = useContext(FirebaseContext);
  const [gameResults, setGameResults] = useState<gameResult[]>([]);

  useEffect(() => {
    return onSnapshot(
      queryGameUpdates(ctx.db, orderBy('timestamp', 'desc')),
      (snapshot) => {
        const updates: Dict<gameUpdate> = toDict(snapshot);
        const rows = Object.keys(updates)
          .filter((key) => !(key in sentinelType) && updates[key]!.timestamp !== null)
          .map((key) => ({ docId: key, result: updates[key] as gameResult }));
        const { results } = dedupeGameResults(rows);
        setGameResults(sortGameResultsRecentFirst(results));
      },
      (error) => {
        console.error(error);
      },
    );
  }, [ctx.db]);

  return gameResults;
}
